<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex mb-2 justify-content-end align-items-start">
                        <span class="flex-grow-1" v-if="name"><b>{{ $t('availabilityIntervals.intervalIntro') }}</b> {{ name }}</span>
                    </div>
                    <div class="d-flex flex-wrap">
                        <!-- Experience Selection -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.experienceMandatory') }}</label>
                            <SingleOptionDropdown 
                                :value="interval.experienceId && interval.experienceId > 0 ? interval.experienceId.toString() : null" 
                                @input="val => interval.experienceId = val ? parseInt(val) : 0" 
                                :options="experiences" 
                                :title="$t('availabilityIntervals.selectExperience')" 
                                :index="1" 
                                :is-valid="fieldValidator('ExperienceId')" 
                                :invalid-feedback="errorOutput['ExperienceId'] || (triedSubmit && !interval.experienceId ? $t('availabilityIntervals.experienceMandatory') : '')">
                            </SingleOptionDropdown>
                        </div>

                        <!-- Guide Selection -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.guideMandatory') }}</label>
                            <SingleOptionDropdown 
                                :value="interval.guideId && interval.guideId > 0 ? interval.guideId.toString() : null" 
                                @input="val => interval.guideId = val ? parseInt(val) : 0" 
                                :options="guides" 
                                :title="$t('availabilityIntervals.selectGuide')" 
                                :index="2" 
                                :is-valid="fieldValidator('GuideId')" 
                                :invalid-feedback="errorOutput['GuideId'] || (triedSubmit && !interval.guideId ? $t('availabilityIntervals.guideMandatory') : '')">
                            </SingleOptionDropdown>
                        </div>

                        <!-- Start Date -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.startDateMandatory') }}</label>
                            <CInput 
                                type="date" 
                                :value="startDateString" 
                                @input="val => interval.startDate = val"
                                :invalid-feedback="errorOutput['StartDate'] || (triedSubmit && !interval.startDate ? $t('availabilityIntervals.startDateMandatory') : '')"
                                :is-valid="fieldValidator('StartDate')">
                            </CInput>
                        </div>

                        <!-- End Date -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.endDateMandatory') }}</label>
                            <CInput 
                                type="date" 
                                :value="endDateString" 
                                @input="val => interval.endDate = val"
                                :invalid-feedback="errorOutput['EndDate'] || (triedSubmit && !interval.endDate ? $t('availabilityIntervals.endDateMandatory') : '')"
                                :is-valid="fieldValidator('EndDate')">
                            </CInput>
                        </div>

                        <!-- Start Time -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.startTimeMandatory') }}</label>
                            <CInput 
                                type="time" 
                                v-model="interval.startTime" 
                                :invalid-feedback="errorOutput['StartTime'] || (triedSubmit && !interval.startTime ? $t('availabilityIntervals.startTimeMandatory') : '')"
                                :is-valid="fieldValidator('StartTime')">
                            </CInput>
                        </div>

                        <!-- Frequency -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.frequencyMandatory') }}</label>
                            <select 
                                class="form-control"
                                :class="{ 'is-invalid': triedSubmit && fieldValidator('Frequency') === false }"
                                v-model="interval.frequency">
                                <option :value="1">{{ $t('availabilityIntervals.frequencyDaily') }}</option>
                                <option :value="2">{{ $t('availabilityIntervals.frequencyWeekdays') }}</option>
                                <option :value="3">{{ $t('availabilityIntervals.frequencyWeekends') }}</option>
                                <option :value="4">{{ $t('availabilityIntervals.frequencyMonday') }}</option>
                                <option :value="5">{{ $t('availabilityIntervals.frequencyTuesday') }}</option>
                                <option :value="6">{{ $t('availabilityIntervals.frequencyWednesday') }}</option>
                                <option :value="7">{{ $t('availabilityIntervals.frequencyThursday') }}</option>
                                <option :value="8">{{ $t('availabilityIntervals.frequencyFriday') }}</option>
                                <option :value="9">{{ $t('availabilityIntervals.frequencySaturday') }}</option>
                                <option :value="10">{{ $t('availabilityIntervals.frequencySunday') }}</option>
                            </select>
                            <div v-if="triedSubmit && fieldValidator('Frequency') === false" class="invalid-feedback d-block">
                                {{ errorOutput['Frequency'] || $t('availabilityIntervals.frequencyMandatory') }}
                            </div>
                        </div>

                        <!-- Min People -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.minPeopleMandatory') }}</label>
                            <CInput 
                                type="number" 
                                v-model="interval.minPeople" 
                                :invalid-feedback="errorOutput['MinPeople'] || (triedSubmit && (!interval.minPeople || interval.minPeople <= 0) ? $t('availabilityIntervals.minPeopleMandatory') : '')"
                                :is-valid="fieldValidator('MinPeople')">
                            </CInput>
                        </div>

                        <!-- Max People -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('availabilityIntervals.maxPeopleMandatory') }}</label>
                            <CInput 
                                type="number" 
                                v-model="interval.maxPeople" 
                                :invalid-feedback="errorOutput['MaxPeople'] || (triedSubmit && (!interval.maxPeople || interval.maxPeople <= 0) ? $t('availabilityIntervals.maxPeopleMandatory') : '')"
                                :is-valid="fieldValidator('MaxPeople')">
                            </CInput>
                        </div>

                    </div>
                    <div role="group" class="form-group col-12 d-flex justify-content-end">
                        <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                    </div>
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup"
            v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>

<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import AvailabilityInterval from "@/classes/AvailabilityInterval";
import { mapState, mapActions } from 'vuex';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue';
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue';
import InputGetList from "@/classes/InputGetList";

export default {
    components: { ConfirmationPopUp, FormLoading, SingleOptionDropdown },
    props: {
        baseInterval: {
            type: Object,
            default: null,
        },
        baseIntervalName: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            triedSubmit: false,
            interval: new AvailabilityInterval(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        };
    },
    mounted() {
        this.loadExperiences();
        this.loadGuides();
        this.resetInterval();
    },
    computed: {
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('experiences', ['getExperiencesOutput']),
        ...mapState('guides', ['getGuidesOutput']),
        ...mapState('availabilityIntervals', ['createAvailabilityIntervalOutput']),
        ...mapState('availabilityIntervals', ['updateAvailabilityIntervalOutput']),
        ...mapState('availabilityIntervals', ['generateSlotsOutput']),
        errorOutput() {
            var result = [];
            if (this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
                result = this.alertOutput.reduce((accum, currentVal) => {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }
            return result;
        },
        experiences() {
            var result = [];
            if (this.getExperiencesOutput && this.getExperiencesOutput.data) {
                this.getExperiencesOutput.data.forEach(experience => {
                    result.push({ key: experience.id.toString(), label: experience.name });
                });
            }
            return result;
        },
        guides() {
            var result = [];
            if (this.getGuidesOutput && this.getGuidesOutput.data) {
                this.getGuidesOutput.data.forEach(guide => {
                    result.push({ key: guide.id.toString(), label: guide.name });
                });
            }
            return result;
        },
        startDateString() {
            const v = this.interval && this.interval.startDate ? this.interval.startDate : null;
            if (!v) return '';
            if (v instanceof Date) return v.toISOString().split('T')[0];
            if (typeof v === 'string') return v.split('T')[0];
            return '';
        },
        endDateString() {
            const v = this.interval && this.interval.endDate ? this.interval.endDate : null;
            if (!v) return '';
            if (v instanceof Date) return v.toISOString().split('T')[0];
            if (typeof v === 'string') return v.split('T')[0];
            return '';
        },
        name() {
            return this.baseInterval || this.baseIntervalName ? (this.baseInterval ? this.baseInterval.experience?.name : this.baseIntervalName) : null;
        }
    },
    methods: {
        ...mapActions('experiences', ['getExperiences']),
        ...mapActions('guides', ['getGuides']),
        loadExperiences() {
            var input = new InputGetList(999, 1, null, 1, "id");
            this.getExperiences(input);
        },
        loadGuides() {
            var input = new InputGetList(999, 1, null, 1, "id");
            this.getGuides(input);
        },
        resetInterval() {
            this.triedSubmit = false; // Reset validation state when resetting interval
            if (!this.baseInterval) {
                this.interval = new AvailabilityInterval();
                // Opened from an experience's price period: prefill the experience and the period's dates
                const query = this.$route.query || {};
                if (query.experienceId) this.interval.experienceId = parseInt(query.experienceId) || 0;
                if (query.startDate) this.interval.startDate = String(query.startDate).substring(0, 10);
                if (query.endDate) this.interval.endDate = String(query.endDate).substring(0, 10);
            } else {
                this.interval = deepClone(new AvailabilityInterval(), this.baseInterval);
                // Normalize time fields for HTML time inputs (expect HH:mm)
                if (this.interval && this.interval.startTime) {
                    const s = this.interval.startTime.toString();
                    this.interval.startTime = s.length >= 5 ? s.substring(0,5) : s;
                }
                // Ensure dates are yyyy-MM-dd strings
                if (this.interval && this.interval.startDate) {
                    const d = this.interval.startDate;
                    if (d instanceof Date) {
                        this.interval.startDate = d.toISOString().split('T')[0];
                    } else if (typeof d === 'string') {
                        this.interval.startDate = d.split('T')[0];
                    }
                }
                if (this.interval && this.interval.endDate) {
                    const d = this.interval.endDate;
                    if (d instanceof Date) {
                        this.interval.endDate = d.toISOString().split('T')[0];
                    } else if (typeof d === 'string') {
                        this.interval.endDate = d.split('T')[0];
                    }
                }
            }
        },
        submitClicked() {
            this.triedSubmit = true;
            if (this.interval.isValid()) {
                this.submitting = true;
                // Always set isActive to true when creating/updating
                // (archiving is done separately through the archive action)
                const payload = {
                    ...this.interval,
                    isActive: true, // Always active when created/updated through form
                    startDate: this.interval.getFormattedStartDate(),
                    endDate: this.interval.getFormattedEndDate(),
                    startTime: this.interval.getFormattedStartTime()
                };
                this.$emit('submit-requested', payload);
            }
        },
        genericValidator,
        errorValidator,
        fieldValidator(fieldName) {
            // First check server errors
            if (this.errorOutput && this.errorOutput[fieldName]) {
                return false;
            }
            
            // Then check local validation if triedSubmit is true
            if (this.triedSubmit) {
                const interval = this.interval;
                switch(fieldName) {
                    case 'ExperienceId':
                        return interval.experienceId > 0;
                    case 'GuideId':
                        return interval.guideId > 0;
                    case 'StartDate':
                        return interval.startDate && interval.startDate !== '';
                    case 'EndDate':
                        return interval.endDate && interval.endDate !== '';
                    case 'StartTime':
                        return interval.startTime && interval.startTime !== '';
                    case 'Frequency':
                        return interval.frequency && interval.frequency > 0;
                    case 'MinPeople':
                        return interval.minPeople > 0;
                    case 'MaxPeople':
                        return interval.maxPeople > 0;
                    default:
                        return undefined;
                }
            }
            
            return undefined; // Don't show validation state until triedSubmit
        },
        onClickConfirmationPopup(value) {
            this.showConfirmationPopup = value;
            this.$router.push({ name: "AvailabilityIntervalsList" });
        },
    },
    watch: {
        createAvailabilityIntervalOutput(val) {
            // Don't show message here, wait for generateSlotsOutput
            // The message will be shown by generateSlotsOutput watcher
        },
        updateAvailabilityIntervalOutput(val) {
            // Don't show message here, wait for generateSlotsOutput
            // The message will be shown by generateSlotsOutput watcher
        },
        generateSlotsOutput(val) {
            if (val && val.slotsCreated !== undefined) {
                const slotsCount = val.slotsCreated || 0;
                if (this.baseInterval) {
                    // Edit mode
                    this.confirmationMessage = this.$t('availabilityIntervals.saveSuccess') + '. ' + 
                        this.$i18n.tc('availabilityIntervals.slotsGeneratedSuccess', slotsCount, { count: slotsCount });
                } else {
                    // Create mode
                    this.confirmationMessage = this.$t('availabilityIntervals.createSuccess') + '. ' + 
                        this.$i18n.tc('availabilityIntervals.slotsGeneratedSuccess', slotsCount, { count: slotsCount });
                }
                this.showConfirmationPopup = true;
            }
        },
        baseInterval() {
            this.resetInterval();
        },
        alertType() {
            this.submitting = false;
        },
        alertOutput() {
            this.submitting = false;
            if (typeof this.alertOutput === 'string' || this.alertOutput instanceof String) {
                alert(this.alertOutput);
            }
        },
    }
};
</script>

<style scoped>
.showDropdown {
    display: block !important;
}

.input-group-append {
    height: fit-content;
}
</style>

